
import { useState } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CalendarDateRangePicker } from '@/components/analytics/DateRangePicker';
import { MetricCard } from '@/components/analytics/MetricCard';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Download, Filter, LineChart, BarChart3, ShieldCheck } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart as ReLineChart, Line, PieChart, Pie, Cell } from 'recharts';

// Sample ESG data for the dashboard
const environmentalData = [
  { month: 'Jan', carbonFootprint: 25, renewableEnergy: 45, wasteReduction: 30 },
  { month: 'Feb', carbonFootprint: 23, renewableEnergy: 48, wasteReduction: 35 },
  { month: 'Mar', carbonFootprint: 20, renewableEnergy: 52, wasteReduction: 38 },
  { month: 'Apr', carbonFootprint: 19, renewableEnergy: 55, wasteReduction: 40 },
  { month: 'May', carbonFootprint: 18, renewableEnergy: 58, wasteReduction: 42 },
  { month: 'Jun', carbonFootprint: 15, renewableEnergy: 62, wasteReduction: 45 },
];

const socialInitiatives = [
  { name: 'Diversity & Inclusion', score: 85 },
  { name: 'Employee Well-being', score: 78 },
  { name: 'Community Outreach', score: 65 },
  { name: 'Work-Life Balance', score: 82 },
  { name: 'Training & Development', score: 74 },
];

const governanceData = [
  { category: 'Policy Compliance', value: 92 },
  { category: 'Board Diversity', value: 68 },
  { category: 'Ethics Training', value: 85 },
  { category: 'Transparency', value: 78 },
  { category: 'Risk Management', value: 88 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function ESGDashboard() {
  const [activeTab, setActiveTab] = useState('environmental');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">ESG Dashboard</h2>
            <p className="text-muted-foreground">
              Track environmental, social, and governance performance metrics
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <CalendarDateRangePicker />
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
        <Separator />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            title="Carbon Footprint"
            value="-15%"
            description="Reduction in CO2 emissions"
            trend={5}
            trendLabel="vs last quarter"
            icon={BarChart3}
          />
          <MetricCard
            title="Social Impact Score"
            value="76/100"
            description="Based on community initiatives"
            trend={3}
            trendLabel="vs last quarter"
            icon={LineChart}
          />
          <MetricCard
            title="Governance Rating"
            value="A-"
            description="Compliance and ethics score"
            trend={1}
            trendLabel="vs last quarter"
            icon={ShieldCheck}
          />
        </div>

        <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="environmental">Environmental</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="governance">Governance</TabsTrigger>
          </TabsList>
          
          <TabsContent value="environmental" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Environmental Performance Metrics</CardTitle>
                <CardDescription>
                  Tracking carbon footprint, renewable energy usage, and waste reduction
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <ReLineChart
                    data={environmentalData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="carbonFootprint" 
                      name="Carbon Footprint (tons)" 
                      stroke="#8884d8" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="renewableEnergy" 
                      name="Renewable Energy (%)" 
                      stroke="#82ca9d" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="wasteReduction" 
                      name="Waste Reduction (%)" 
                      stroke="#ffc658" 
                    />
                  </ReLineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Carbon Footprint Reduction</CardTitle>
                  <CardDescription>
                    Progress toward annual reduction targets
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { quarter: 'Q1', target: 30, actual: 25 },
                          { quarter: 'Q2', target: 25, actual: 20 },
                          { quarter: 'Q3', target: 20, actual: 18 },
                          { quarter: 'Q4', target: 15, actual: 15 },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="quarter" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="target" fill="#8884d8" name="Target (tons CO2)" />
                        <Bar dataKey="actual" fill="#82ca9d" name="Actual (tons CO2)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Energy Sources</CardTitle>
                  <CardDescription>
                    Distribution of energy usage by source
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Solar', value: 35 },
                            { name: 'Wind', value: 25 },
                            { name: 'Hydroelectric', value: 15 },
                            { name: 'Natural Gas', value: 20 },
                            { name: 'Coal', value: 5 },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value}%`, 'Energy Source']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="social" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Social Initiatives Performance</CardTitle>
                <CardDescription>
                  Evaluation of team's social responsibility initiatives
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={socialInitiatives}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value) => [`${value}/100`, 'Score']} />
                    <Legend />
                    <Bar 
                      dataKey="score" 
                      fill="#8884d8" 
                      name="Performance Score" 
                      label={{ position: 'top', fill: '#666' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Diversity & Inclusion</CardTitle>
                  <CardDescription>
                    Team composition metrics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Gender Diversity', value: 45 },
                            { name: 'Ethnic Diversity', value: 30 },
                            { name: 'Age Diversity', value: 15 },
                            { name: 'Disability Inclusion', value: 10 },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value}%`, 'Category']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Community Engagement</CardTitle>
                  <CardDescription>
                    Hours contributed to community initiatives
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ReLineChart
                        data={[
                          { month: 'Jan', hours: 120 },
                          { month: 'Feb', hours: 145 },
                          { month: 'Mar', hours: 135 },
                          { month: 'Apr', hours: 160 },
                          { month: 'May', hours: 180 },
                          { month: 'Jun', hours: 210 },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value} hrs`, 'Volunteer Time']} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="hours" 
                          stroke="#82ca9d" 
                          name="Volunteer Hours" 
                        />
                      </ReLineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="governance" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Governance Performance</CardTitle>
                <CardDescription>
                  Metrics for organizational governance and compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={governanceData}
                    layout="vertical"
                    margin={{
                      top: 20,
                      right: 30,
                      left: 100,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="category" width={100} />
                    <Tooltip formatter={(value) => [`${value}/100`, 'Score']} />
                    <Legend />
                    <Bar 
                      dataKey="value" 
                      fill="#8884d8" 
                      name="Performance Score" 
                      label={{ position: 'right', fill: '#666' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Compliance Tracking</CardTitle>
                  <CardDescription>
                    Policy compliance over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ReLineChart
                        data={[
                          { quarter: 'Q1 2022', compliance: 85 },
                          { quarter: 'Q2 2022', compliance: 87 },
                          { quarter: 'Q3 2022', compliance: 90 },
                          { quarter: 'Q4 2022', compliance: 89 },
                          { quarter: 'Q1 2023', compliance: 92 },
                          { quarter: 'Q2 2023', compliance: 94 },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="quarter" />
                        <YAxis domain={[80, 100]} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Compliance Rate']} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="compliance" 
                          stroke="#8884d8" 
                          name="Compliance Rate" 
                        />
                      </ReLineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Risk Assessment</CardTitle>
                  <CardDescription>
                    Governance risk factors evaluation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { category: 'Regulatory', risk: 25 },
                          { category: 'Financial', risk: 35 },
                          { category: 'Operational', risk: 20 },
                          { category: 'Reputational', risk: 15 },
                          { category: 'Strategic', risk: 30 },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Risk Level']} />
                        <Legend />
                        <Bar dataKey="risk" fill="#ff8042" name="Risk Level" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
